import { onMounted, onUnmounted, readonly, ref, type Ref } from 'vue';

// The cascade's desktop/narrow split, in one place. Keep in sync with the
// `tabletLandscape` Tailwind breakpoint and the media queries in
// CascadeColumnStrip.vue: below it the strip is a fixed full-screen push-nav
// overlay showing one column at a time, at or above it the columns sit side by
// side in a horizontal scroller.
export const TABLET_LANDSCAPE_PX = 1024;

/**
 * The same question outside a component. `useIsNarrow` cannot be called from a
 * store or a plain function — it registers `onMounted`/`onUnmounted` hooks — but
 * `sidebarStore.openSidebar` has to know the mode to decide whether a create flow
 * opens in the draft zone (desktop) or as a `:new` push-nav column (narrow), and
 * that decision is made once per click rather than tracked reactively.
 */
export const isNarrowViewport = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth < TABLET_LANDSCAPE_PX;

const readIsNarrow = (): boolean => isNarrowViewport();

/**
 * Tracks whether the viewport is below the cascade's desktop breakpoint.
 *
 * Seeded synchronously during setup rather than in `onMounted`, so a component
 * that renders differently per mode (the column rail, the panel header's
 * side-by-side affordances) gets the right markup on its first paint instead of
 * flashing the desktop variant for a frame.
 */
export function useIsNarrow(): Readonly<Ref<boolean>> {
  const isNarrow = ref<boolean>(readIsNarrow());
  const sync = (): void => {
    isNarrow.value = readIsNarrow();
  };
  onMounted(() => window.addEventListener('resize', sync));
  onUnmounted(() => window.removeEventListener('resize', sync));
  return readonly(isNarrow);
}
